import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Bot, Cpu, Shield, ShieldAlert, Sparkles, TestTube2 } from "lucide-react";
import CriteriaBar from "./CriteriaBar";

const TARGET_META = {
  gpt: { label: "GPT", icon: Cpu },
  claude: { label: "Claude", icon: Bot },
  gemini: { label: "Gemini", icon: Sparkles },
  generic: { label: null, icon: Bot },
};

const RISK_META = {
  low: { color: "rgba(255,255,255,0.55)", bg: "rgba(255,255,255,0.05)", icon: Shield },
  medium: { color: "#F4D77A", bg: "rgba(244,215,122,0.12)", icon: ShieldAlert },
  high: { color: "#F87171", bg: "rgba(248,113,113,0.12)", icon: ShieldAlert },
};

function useAnimatedNumber(target, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0;
    const to = Number(target) || 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function scoreColor(score) {
  if (score >= 9) return "#D4AF37";
  if (score >= 7) return "#1E40AF";
  return "#7F1D1D";
}

export function AuditReport({ result, onShowCertificate }) {
  const { t } = useTranslation();
  const score = Number(result.nexa_score) || 0;
  const animated = useAnimatedNumber(score);
  const color = scoreColor(score);
  const optimized = result.status === "Nexa Optimized";

  const target = result.detected_target_model || "generic";
  const targetMeta = TARGET_META[target] || TARGET_META.generic;
  const TargetIcon = targetMeta.icon;
  const targetLabel = targetMeta.label || t("result.targetGeneric");

  const risk = result.hallucination_risk || "low";
  const riskMeta = RISK_META[risk] || RISK_META.low;
  const RiskIcon = riskMeta.icon;
  const riskLabel = t(`result.risk${risk.charAt(0).toUpperCase()}${risk.slice(1)}`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 0.9, 0.36, 1] }}
      className="grid gap-7"
      data-testid="audit-report"
    >
      {/* DEMO MODE banner — muted grey pill, appears only when the audit
          state was produced by the frontend-only [DEMO_MODE] trigger. */}
      {result.__demo_mode ? (
        <div
          className="inline-flex w-fit items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-white/55"
          data-testid="audit-demo-banner"
        >
          <TestTube2 className="h-3.5 w-3.5 text-white/45" />
          <span>{t("auditor.demo.banner")}</span>
        </div>
      ) : null}

      {/* Header: score + status */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-white/45">
            {t("result.score")}
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span
              className="font-display text-6xl sm:text-7xl tracking-tight tabular-nums"
              style={{ color, textShadow: `0 0 24px ${color}33` }}
              data-testid="audit-score"
            >
              {animated.toFixed(1)}
            </span>
            <span className="text-sm text-white/40">/ 10</span>
          </div>
        </div>
        <span
          data-testid="audit-status-badge"
          className={
            "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold border " +
            (optimized
              ? "border-[#D4AF37]/50 bg-[#D4AF37]/15 text-[#F4D77A]"
              : "border-white/10 bg-white/[0.04] text-white/85")
          }
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: optimized ? "#D4AF37" : "#1E40AF" }}
          />
          {optimized ? t("result.statusOptimized") : t("result.statusRevision")}
        </span>
      </div>

      {/* Pills row: target + risk */}
      <div className="flex flex-wrap items-center gap-2.5" data-testid="audit-pills">
        <span
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/75"
          data-testid="audit-target-pill"
        >
          <TargetIcon className="h-3.5 w-3.5 text-white/60" />
          <span className="text-white/55">{t("result.target")}:</span>
          <span className="font-medium text-white">{targetLabel}</span>
        </span>
        <span
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs"
          style={{ borderColor: riskMeta.color + "55", background: riskMeta.bg, color: riskMeta.color }}
          data-testid="audit-risk-pill"
        >
          <RiskIcon className="h-3.5 w-3.5" />
          <span className="opacity-75">{t("result.risk")}:</span>
          <span className="font-semibold">{riskLabel}</span>
        </span>
        {result.fallback ? (
          <span
            className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-200"
            data-testid="audit-fallback-pill"
          >
            {t("auditor.footerFallback")}
          </span>
        ) : null}
      </div>

      {/* Criteria breakdown */}
      <div>
        <div className="mb-3 text-xs uppercase tracking-[0.16em] text-white/45">
          {t("result.criteria")}
        </div>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="audit-criteria">
          <CriteriaBar
            criterionKey="clarity"
            label={t("result.clarity")}
            value={result.criteria?.clarity}
            delay={0}
          />
          <CriteriaBar
            criterionKey="constraints"
            label={t("result.constraints")}
            value={result.criteria?.constraints}
            delay={0.08}
          />
          <CriteriaBar
            criterionKey="efficiency"
            label={t("result.efficiency")}
            value={result.criteria?.efficiency}
            delay={0.16}
          />
          <CriteriaBar
            criterionKey="resilience"
            label={t("result.resilience")}
            value={result.criteria?.resilience}
            delay={0.24}
          />
        </div>
      </div>

      {/* Stress test — neon red */}
      <div data-testid="audit-stress-test">
        <div className="mb-2 flex items-center gap-2">
          <span
            className="h-2 w-2 rounded-full"
            style={{
              background: "#FF1744",
              boxShadow: "0 0 8px #FF1744, 0 0 16px rgba(255,23,68,0.55)",
            }}
          />
          <span
            className="text-xs uppercase tracking-[0.16em] font-semibold bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(90deg, #FF1744 0%, #FF7A1A 100%)",
            }}
          >
            {t("result.stressTest")}
          </span>
        </div>
        <ul className="space-y-2">
          {(result.stress_test || []).map((item, i) => (
            <li
              key={i}
              className="flex gap-2.5 text-sm leading-relaxed text-white/85"
            >
              <span
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
                style={{
                  background: "#FF1744",
                  boxShadow: "0 0 8px #FF1744, 0 0 14px rgba(255,23,68,0.6)",
                }}
                aria-hidden
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Suggestions */}
      <div data-testid="audit-suggestions">
        <div className="mb-2 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
          <span className="text-xs uppercase tracking-[0.16em] text-white/55">
            {t("result.suggestions")}
          </span>
        </div>
        <ul className="space-y-2">
          {(result.suggestions || []).map((item, i) => (
            <li
              key={i}
              className="flex gap-2.5 text-sm leading-relaxed text-white/85"
            >
              <span className="mt-1 text-[#D4AF37]">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Mode footer */}
      <div className="mt-1 flex flex-wrap items-center justify-between gap-3 border-t border-white/5 pt-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-white/40">
          <span className="uppercase tracking-[0.16em]">
            {t("auditor.footerMode")}:
          </span>
          <span className="text-white/65">
            {result.mode === "deep"
              ? t("auditor.footerDeepMode")
              : t("auditor.footerQuickMode")}
          </span>
          <span>·</span>
          <span>{t("auditor.elapsedMs", { ms: result.elapsed_ms ?? 0 })}</span>
        </div>
        {onShowCertificate ? (
          <button
            type="button"
            onClick={onShowCertificate}
            className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:text-[#F4D77A] transition-colors"
            data-testid="audit-show-certificate-btn"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
            {t("cert.showCertificate")}
          </button>
        ) : null}
      </div>
    </motion.div>
  );
}

export default AuditReport;
